/**
 * autoRes — a DECISÃO da resolução automática, isolada do DOM/WebGL.
 *
 * É o mesmo sistema de sempre (SceneManager.setAutoResolution/_tickAutoRes chamam
 * isto); só a lógica mora aqui para o harness poder dirigi-la deterministicamente
 * (scripts/autoResHarness.mjs) — os três defeitos da primeira versão passaram
 * despercebidos exatamente porque a decisão estava enterrada no loop de render.
 *
 * O QUE MUDOU em relação à versão com defeitos:
 *
 * 1. A amostra é o TEMPO OCUPADO do quadro (busyMs: do início do tick ao fim do
 *    render + overlay), não o dt. O dt de um monitor 60 Hz com vsync é ~16,7 ms
 *    MESMO com a máquina folgada — medir por ele tornava a recuperação impossível
 *    (defeito 1: o limiar de subida pedia >80 fps). O busy de uma máquina folgada
 *    é bem menor que o período do vsync, então a folga volta a ser visível.
 *
 * 2. O ORÇAMENTO respeita o fpsCap como intencional (defeito 2): com ?fps=30 o
 *    orçamento é ~33,3 ms — antes ficava em 16,7 ms e todo quadro "parecia ruim".
 *    Sem fpsCap, vale o alvo configurável (targetFps, padrão 30: o objetivo em
 *    PC fraco é sustentar 30, não 60). O dt ainda participa, mas só como detector
 *    de SLOT PERDIDO (dt > 1,25× o orçamento = o quadro não coube), que funciona
 *    igual com e sem teto.
 *
 * 3. Quem aplica o degrau é um PERFIL completo (pr + bloom + amostras do focus
 *    blur — ver AUTO_LADDER), pelo mesmo _applyProfile dos perfis manuais
 *    (defeito 3: antes só mexia no pixelRatio, e o degrau mais baixo do automático
 *    saía mais caro que o perfil "desempenho").
 *
 * Janela e robustez: as amostras acumulam ~1 s de quadros RENDERIZADOS e a janela
 * é avaliada pelo p75 do busy (percentil: um spike isolado de GC não contamina) +
 * a fração de slots perdidos. Aba oculta, contexto perdido ou pausa longa
 * (dt > 250 ms) DESCARTAM a janela em curso — nada disso é sinal de carga.
 *
 * Histerese/cooldown (inalterados, já eram bons): descer exige 3 janelas ruins
 * seguidas e 3 s desde a última troca; subir exige 6 janelas boas e 12 s. Trocar
 * de degrau realoca os render targets (engasgo visível), então a escada é lenta
 * de propósito — e assimétrica: desce rápido, sobe com desconfiança.
 */

/**
 * Escada da resolução automática, do melhor para o mais barato. Cada degrau é um
 * PERFIL completo (mesmos campos dos perfis manuais de QUALITY): 1.0 e 0.9 espelham
 * "alta" e "equilibrada"; 0.75 espelha "desempenho"; 0.85 é o intermediário — o
 * bloom acompanha a resolução e o focus blur já cai p/ 1 anel (a metade barata)
 * um degrau antes do fundo.
 */
export const AUTO_LADDER = [
  { pr: 1.0, bloom: 0.5, rings: 2, aa: 'smaa' },
  { pr: 0.9, bloom: 0.42, rings: 2, aa: 'smaa' },
  { pr: 0.85, bloom: 0.36, rings: 1, aa: 'fxaa' },
  { pr: 0.75, bloom: 0.32, rings: 1, aa: 'fxaa' },
];

const WINDOW_MS = 1000;   // uma janela ≈ 1 s de quadros renderizados
const PAUSE_MS = 250;     // gap acima disto = pausa (aba, GC monstro, breakpoint)
const BAD_TO_DROP = 3;    // janelas ruins seguidas para DESCER
const GOOD_TO_RISE = 6;   // janelas boas seguidas para SUBIR
const DROP_COOLDOWN_S = 3;
const RISE_COOLDOWN_S = 12;
const BUSY_BAD = 1.05;    // p75 do busy acima de 105% do orçamento = janela ruim
const BUSY_GOOD = 0.6;    // p75 do busy abaixo de 60% do orçamento = janela boa
const MISS_FRAC_BAD = 0.2;   // >20% dos quadros perdendo o slot = janela ruim
const MISS_FRAC_GOOD = 0.05; // <5% p/ contar como boa
const MISS_DT = 1.25;     // dt > 125% do orçamento = quadro perdeu o slot

export class AutoResController {
  constructor({ targetFps = 30 } = {}) {
    this.on = false;
    this.targetFps = targetFps; // alvo SEM fpsCap (PC fraco: sustentar 30)
    this.idx = 0;
    this.bad = 0;
    this.good = 0;
    this.lastChange = 0;
    this._busy = [];   // amostras de busyMs da janela em curso
    this._winMs = 0;   // soma de dt renderizado na janela
    this._missed = 0;  // quadros que perderam o slot na janela
  }

  /** Orçamento do quadro: o fpsCap é um teto INTENCIONAL — vira o orçamento. */
  budgetMs(fpsCap) {
    return fpsCap ? 1000 / fpsCap : 1000 / this.targetFps;
  }

  /** Liga/desliga e alinha o degrau ao pixelRatio atual (vindo do perfil manual). */
  enable(on, currentPr, nowS, targetFps) {
    this.on = !!on;
    if (targetFps) this.targetFps = targetFps;
    this._resetWindow();
    this.bad = 0;
    this.good = 0;
    if (this.on) {
      // degrau mais próximo do pixelRatio vigente (perfil manual pode ser 0.75/0.9/1.0)
      let melhor = 0, dist = Infinity;
      AUTO_LADDER.forEach((q, i) => {
        const d = Math.abs(q.pr - currentPr);
        if (d < dist) { dist = d; melhor = i; }
      });
      this.idx = melhor;
      this.lastChange = nowS;
    }
  }

  _resetWindow() {
    this._busy.length = 0;
    this._winMs = 0;
    this._missed = 0;
  }

  /**
   * Uma amostra por quadro RENDERIZADO.
   * @param {number} o.busyMs      tempo ocupado do quadro (tick → fim do render)
   * @param {number} o.dtMs        gap real desde o último quadro renderizado (SEM clamp)
   * @param {number} o.fpsCap     teto intencional de fps (0 = sem teto)
   * @param {boolean} o.hidden       aba oculta
   * @param {boolean} o.contextLost  contexto WebGL perdido
   * @param {number} o.nowS       relógio monotônico em segundos (p/ cooldowns)
   * @returns {number|null} índice novo em AUTO_LADDER quando o degrau muda; senão null
   */
  sample({ busyMs, dtMs, fpsCap = 0, hidden = false, contextLost = false, nowS }) {
    if (!this.on) return null;
    // aba oculta / contexto perdido / pausa longa: não é carga — descarta a janela
    // em curso inteira (uma janela meio contaminada enganaria o percentil)
    if (hidden || contextLost || dtMs > PAUSE_MS) {
      this._resetWindow();
      return null;
    }
    const budget = this.budgetMs(fpsCap);
    this._busy.push(busyMs);
    this._winMs += dtMs;
    if (dtMs > budget * MISS_DT) this._missed++;
    if (this._winMs < WINDOW_MS) return null;

    // fecha a janela: p75 do busy + fração de slots perdidos
    const n = this._busy.length;
    const ordenado = this._busy.slice().sort((a, b) => a - b);
    const p75 = ordenado[Math.min(n - 1, Math.floor(n * 0.75))];
    const missFrac = this._missed / n;
    this._resetWindow();

    if (p75 > budget * BUSY_BAD || missFrac > MISS_FRAC_BAD) {
      this.bad++;
      this.good = 0;
    } else if (p75 < budget * BUSY_GOOD && missFrac < MISS_FRAC_GOOD) {
      this.good++;
      this.bad = 0;
    } else {
      this.bad = 0;
      this.good = 0;
    }

    const since = nowS - this.lastChange;
    let next = this.idx;
    if (this.bad >= BAD_TO_DROP && this.idx < AUTO_LADDER.length - 1 && since > DROP_COOLDOWN_S) next = this.idx + 1;
    else if (this.good >= GOOD_TO_RISE && this.idx > 0 && since > RISE_COOLDOWN_S) next = this.idx - 1;
    if (next === this.idx) return null;
    this.idx = next;
    this.bad = 0;
    this.good = 0;
    this.lastChange = nowS;
    return next;
  }
}
